// Read-only-suspension banner. Suspension is genuinely enforced at the rules
// layer (Task 3): a suspended workspace's documents stay READABLE but every
// write is denied with permission-denied. This banner exists so a suspended
// session reads as an intentional read-only state, not a broken app --
// mounted globally in App.js alongside MessagingOnboardingBanner so it is
// visible on every route, not just the ones a background write might fail on.
//
// isCascaded distinguishes a suspended TEAM AGENT workspace (whose own
// account is fine -- their PAYER's account is suspended and the suspension
// cascaded down to every workspace under it, Task 3) from a directly
// suspended account. The copy must never blame the agent for their payer's
// account.

import React from 'react';
import { SUPPORT_EMAIL } from '../../../config/support';

// statusReason is what the admin typed into the lifecycle confirm step,
// captioned "Shown to the customer on their account page" -- the server has
// always returned it and nothing ever displayed it. It is appended rather
// than substituted, so the base explanation of what suspension means is
// intact whether or not an admin bothered to give a reason.
export function bannerCopyFor({ workspaceStatus, isCascaded, statusReason }) {
    if (workspaceStatus !== 'suspended') return '';
    // The cascade case must not blame the team agent -- their own account is
    // fine; their payer's is not.
    const base = isCascaded
        ? "This workspace is suspended because your team's account is suspended. You can view your transactions, contacts, and reports, but you cannot make changes."
        : 'Your KeyFlow account is suspended. You can view your transactions, contacts, and reports, but you cannot make changes.';
    const reason = typeof statusReason === 'string' ? statusReason.trim() : '';
    return reason ? `${base} Reason: ${reason}` : base;
}

export default function AccountStatusBanner({ workspaceStatus, isCascaded, statusReason }) {
    if (workspaceStatus !== 'suspended') return null;
    const copy = bannerCopyFor({ workspaceStatus, isCascaded, statusReason });
    return (
        <div
            role="status"
            className="border-b border-[color:var(--color-danger)]/20 bg-[color:var(--surface-danger)] px-4 py-2 text-sm text-[color:var(--color-danger)] lg:px-6"
        >
            <div className="mx-auto max-w-7xl">
                {copy}{' '}
                {/* Rendered as a link rather than folded into bannerCopyFor's
                    string, so the address is clickable and the copy stays
                    testable as plain prose. */}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                    Contact {SUPPORT_EMAIL}
                </a>
                .
            </div>
        </div>
    );
}
